import React, { useEffect, useState, useCallback } from "react";
import { History, X, Repeat2, Undo2, Plus, Minus } from "lucide-react";
import {
  athletePutStrengthSets,
  athleteGetStrengthSets,
  athleteGetStrengthHistory,
  getStrengthSwapSuggestions,
} from "../../lib/api";
import { toast } from "sonner";

/**
 * StrengthExerciseLogger
 *
 * Per-exercise set logger embedded inside the athlete's workout-log modal.
 * For each exercise on a strength/mobility workout we render N rows (one per
 * prescribed set) with optional `reps` + `weight` inputs. Logging is
 * completely optional — empty rows are discarded server-side.
 *
 * A small "history" button next to the exercise name pops a panel showing the
 * athlete's most recent sessions for the same exercise (matched by normalised
 * name) so they can see what they did last time before deciding what to lift
 * today.
 *
 * Props:
 *   - workout, programId, weekNumber, dayLabel: keys to scope the logs
 *   - existingLogs (optional): preloaded array of strength_set_logs to skip
 *     the initial fetch.
 */

// Best-effort: parse "3" -> 3, "3-5" -> 5 (upper bound), or fall through.
function parsePrescribedSetCount(setsField) {
  if (!setsField) return 1;
  const s = String(setsField).trim();
  const m = s.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (m) return Math.max(1, parseInt(m[2], 10) || 1);
  const single = s.match(/(\d+)/);
  return single ? Math.max(1, parseInt(single[1], 10)) : 1;
}

export default function StrengthExerciseLogger({
  workout,
  programId,
  weekNumber,
  dayLabel,
}) {
  // Map of exercise id -> { exercise_name, block_id, sets:[{reps, weight, weight_unit}] }
  const [byExercise, setByExercise] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const blocks = workout?.strength_blocks || [];
  // Per-exercise swap state: { [exerciseKey]: { name, swapped_from_name } }
  // Seeded from any saved log that already has a swap, and updated when
  // the athlete picks a different exercise from the swap chooser.
  const [swapsByKey, setSwapsByKey] = useState({});
  const [swapTargetKey, setSwapTargetKey] = useState(null); // open chooser for this key

  // Initial fetch — pull any previously saved sets for THIS workout instance
  useEffect(() => {
    let alive = true;
    if (!programId || !weekNumber || !dayLabel || !workout?.id) {
      setLoaded(true);
      return;
    }
    athleteGetStrengthSets(programId, weekNumber, dayLabel, workout.id)
      .then((logs) => {
        if (!alive) return;
        // Index by exercise_id when present, else by normalised name
        const map = {};
        const swaps = {};
        for (const log of logs || []) {
          const key = log.exercise_id || log.exercise_name_norm || log.exercise_name;
          map[key] = log;
          if (log.swapped_from_name && log.exercise_name) {
            swaps[key] = {
              name: log.exercise_name,
              swapped_from_name: log.swapped_from_name,
            };
          }
        }
        setByExercise(map);
        setSwapsByKey(swaps);
      })
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [programId, weekNumber, dayLabel, workout?.id]);

  const saveRow = useCallback(
    async (exercise, block, setRows, swapInfo = null) => {
      try {
        const saved = await athletePutStrengthSets({
          program_id: programId,
          week_number: weekNumber,
          day_label: dayLabel,
          workout_id: workout.id,
          block_id: block?.id || null,
          exercise_id: exercise?.id || null,
          // If the athlete swapped, log the swap name as what they
          // actually did. Otherwise log the prescribed name.
          exercise_name: swapInfo?.name || exercise?.name || "Exercise",
          swapped_from_name: swapInfo?.swapped_from_name || null,
          sets: setRows.map((r, i) => {
            // For banded sets, the "weight" field carries a free-text
            // colour ("Heavy Red", "Green", …). For numeric units (lbs /
            // kg / bw) we still coerce to a number so the math down-
            // stream works as before. Empty values → null on both paths.
            const raw = r.weight;
            const isBand = r.weight_unit === "band";
            let weight = null;
            let weight_text = null;
            if (raw === "" || raw == null) {
              // nothing logged — leave both null
            } else if (isBand) {
              weight_text = String(raw).trim() || null;
            } else {
              const n = Number(raw);
              weight = Number.isFinite(n) ? n : null;
              // Also surface what the athlete typed if it wasn't a clean
              // number — handles "5kg per side" style notes without
              // losing them.
              if (weight === null) weight_text = String(raw).trim() || null;
            }
            return {
              set_number: i + 1,
              reps: r.reps === "" || r.reps == null ? null : Number(r.reps),
              weight,
              weight_text,
              weight_unit: r.weight_unit || "lbs",
            };
          }),
        });
        // If the server returned a deletion (all rows empty), drop locally too.
        if (saved?.deleted) {
          setByExercise((prev) => {
            const next = { ...prev };
            delete next[exercise.id || exercise.name];
            return next;
          });
        } else {
          setByExercise((prev) => ({
            ...prev,
            [exercise.id || exercise.name]: saved,
          }));
        }
      } catch {
        toast.error("Couldn't save — try again");
      }
    },
    [programId, weekNumber, dayLabel, workout?.id]
  );

  // Local-only edit state per exercise (synced with `byExercise` for the
  // initial value but kept editable so each keystroke doesn't fire a save).
  const [localEdits, setLocalEdits] = useState({});
  useEffect(() => {
    // Seed local edits from server data once
    const seed = {};
    for (const block of blocks) {
      for (const ex of block.exercises || []) {
        const key = ex.id || ex.name;
        const log = byExercise[key];
        const saved = log?.sets || [];
        // Always show at least the prescribed sets; if the athlete added
        // extra sets last time, show those too.
        const savedMax = saved.reduce((m, x) => Math.max(m, Number(x.set_number) || 0), 0);
        const count = Math.max(parsePrescribedSetCount(ex.sets), savedMax);
        const rows = [];
        for (let i = 0; i < count; i++) {
          const s = saved.find((x) => x.set_number === i + 1);
          rows.push({
            reps: s?.reps ?? "",
            // For banded sets the colour is in weight_text; for numeric
            // sets `weight` carries the number. Always prefer the text
            // when present so non-numeric notes round-trip too.
            weight: s?.weight_text ?? (s?.weight ?? ""),
            weight_unit: s?.weight_unit || "lbs",
          });
        }
        seed[key] = rows;
      }
    }
    setLocalEdits(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, JSON.stringify(blocks.map((b) => (b.exercises || []).map((e) => e.id || e.name + ":" + e.sets)))]);

  const updateRow = (key, idx, field, value, ex, block) => {
    setLocalEdits((prev) => {
      const next = { ...prev };
      const rows = [...(next[key] || [])];
      rows[idx] = { ...rows[idx], [field]: value };
      next[key] = rows;
      // Debounced save: clear any existing timer and start a new one
      if (window.__strengthSaveTimers == null) window.__strengthSaveTimers = {};
      clearTimeout(window.__strengthSaveTimers[key]);
      window.__strengthSaveTimers[key] = setTimeout(() => {
        saveRow(ex, block, rows, swapsByKey[key] || null);
      }, 700);
      return next;
    });
  };

  // Apply a swap: rename the exercise locally and immediately persist the
  // change (with `swapped_from_name`) so the coach sees what the athlete
  // actually did. We don't wait for the debounced row-save here — the
  // swap is a discrete user action and should save right away even if
  // they haven't touched any set rows yet.
  const applySwap = useCallback(
    (key, ex, block, newName) => {
      const swapInfo = newName
        ? { name: newName, swapped_from_name: ex.name || "" }
        : null;
      setSwapsByKey((prev) => {
        const next = { ...prev };
        if (newName) next[key] = swapInfo;
        else delete next[key];
        return next;
      });
      const rows = localEdits[key] || [];
      // Even when no rows are filled in yet, send the swap so the
      // record gets created (then the coach can see the swap on the
      // activity feed before any sets are logged).
      saveRow(ex, block, rows, swapInfo);
      setSwapTargetKey(null);
    },
    [localEdits, saveRow]
  );

  // Add / remove a set row and persist immediately (sets are replaced
  // wholesale on save, so the server mirrors the new count).
  const changeSetCount = (key, ex, block, delta) => {
    setLocalEdits((prev) => {
      const rows = [...(prev[key] || [])];
      if (delta > 0) {
        const last = rows[rows.length - 1];
        rows.push({ reps: "", weight: "", weight_unit: last?.weight_unit || "lbs" });
      } else if (rows.length > 1) {
        rows.pop();
      } else {
        return prev;
      }
      if (window.__strengthSaveTimers == null) window.__strengthSaveTimers = {};
      clearTimeout(window.__strengthSaveTimers[key]);
      saveRow(ex, block, rows, swapsByKey[key] || null);
      return { ...prev, [key]: rows };
    });
  };

  // History follows the exercise the athlete is ACTUALLY doing — after a
  // swap that's the swapped-in name, not the coach's original.
  const openHistory = async (exercise, key) => {
    const name = swapsByKey[key]?.name || exercise.name || "Exercise";
    setHistoryFor(name);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const data = await athleteGetStrengthHistory(name, 5);
      setHistoryData(data || []);
    } catch {
      toast.error("Couldn't load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  if (!blocks.length) return null;

  return (
    <div className="space-y-4" data-testid="strength-set-logger">
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
        Log what you actually did (optional)
      </div>

      {blocks.map((block) => (
        <div
          key={block.id}
          className="border border-[var(--tir-border)] bg-[var(--tir-ivory)] p-3"
        >
          {block.label && (
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)] mb-2">
              {block.label}
            </div>
          )}
          <div className="space-y-3">
            {(block.exercises || []).map((ex) => {
              const key = ex.id || ex.name;
              const rows = localEdits[key] || [];
              const prescribed = [
                ex.sets ? `${ex.sets} sets` : null,
                ex.reps ? `${ex.reps} reps` : null,
                ex.load ? `@ ${ex.load}` : null,
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <div key={key} data-testid={`set-logger-${key}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-[var(--tir-ink)]">
                        {swapsByKey[key]?.name || ex.name || "Exercise"}
                      </span>
                      {swapsByKey[key] && (
                        <span
                          className="ml-2 text-[9px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] border border-[var(--tir-burgundy)]/40 px-1.5 py-0.5 align-middle"
                          data-testid={`swap-badge-${key}`}
                          title={`Swapped from ${swapsByKey[key].swapped_from_name}`}
                        >
                          Swapped
                        </span>
                      )}
                      {prescribed && (
                        <span className="text-[var(--tir-muted)] text-[11px] font-mono-tir ml-2">
                          {prescribed}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {swapsByKey[key] ? (
                        <button
                          type="button"
                          onClick={() => applySwap(key, ex, block, null)}
                          data-testid={`swap-undo-${key}`}
                          className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)] inline-flex items-center gap-1"
                          title={`Undo swap — back to ${swapsByKey[key].swapped_from_name}`}
                        >
                          <Undo2 size={11} /> Undo
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSwapTargetKey(key)}
                          data-testid={`swap-btn-${key}`}
                          className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-ink)] inline-flex items-center gap-1"
                          title="Swap to a different exercise"
                        >
                          <Repeat2 size={11} /> Swap
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openHistory(ex, key)}
                        data-testid={`history-btn-${key}`}
                        className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-burgundy)] inline-flex items-center gap-1"
                        title="See what you did last time"
                      >
                        <History size={11} /> History
                      </button>
                    </div>
                  </div>
                  <div className="mt-1.5 space-y-1">
                    {rows.map((row, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-[12px]"
                      >
                        <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] w-10 shrink-0">
                          Set {i + 1}
                        </span>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={row.reps}
                          onChange={(e) => updateRow(key, i, "reps", e.target.value, ex, block)}
                          placeholder="reps"
                          data-testid={`set-${key}-${i}-reps`}
                          className="w-14 border border-[var(--tir-border)] bg-white px-1.5 py-1 font-mono-tir text-[12px] focus:outline-none focus:border-[var(--tir-burgundy)]"
                        />
                        <span className="text-[var(--tir-muted)]">×</span>
                        <input
                          type="text"
                          inputMode={row.weight_unit === "band" ? "text" : "decimal"}
                          value={row.weight}
                          onChange={(e) => updateRow(key, i, "weight", e.target.value, ex, block)}
                          placeholder={row.weight_unit === "band" ? "colour" : "wt"}
                          data-testid={`set-${key}-${i}-weight`}
                          // Wider when banded — colour names like "Heavy Red" need room.
                          className={`${row.weight_unit === "band" ? "w-24" : "w-16"} border border-[var(--tir-border)] bg-white px-1.5 py-1 font-mono-tir text-[12px] focus:outline-none focus:border-[var(--tir-burgundy)]`}
                        />
                        <select
                          value={row.weight_unit}
                          onChange={(e) => updateRow(key, i, "weight_unit", e.target.value, ex, block)}
                          data-testid={`set-${key}-${i}-unit`}
                          className="border border-[var(--tir-border)] bg-white px-1 py-1 text-[10px] tracking-[0.1em] uppercase font-mono-tir focus:outline-none"
                        >
                          <option value="lbs">lbs</option>
                          <option value="kg">kg</option>
                          <option value="bw">BW</option>
                          <option value="band">band</option>
                        </select>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-0.5">
                      <button
                        type="button"
                        onClick={() => changeSetCount(key, ex, block, -1)}
                        disabled={rows.length <= 1}
                        data-testid={`set-remove-${key}`}
                        className="h-6 px-2 border border-[var(--tir-border)] bg-white text-[10px] tracking-[0.16em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)] hover:border-[var(--tir-ink)] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                        title="Remove last set"
                      >
                        <Minus size={10} /> Set
                      </button>
                      <button
                        type="button"
                        onClick={() => changeSetCount(key, ex, block, 1)}
                        data-testid={`set-add-${key}`}
                        className="h-6 px-2 border border-[var(--tir-border)] bg-white text-[10px] tracking-[0.16em] uppercase font-semibold text-[var(--tir-burgundy)] hover:border-[var(--tir-burgundy)] inline-flex items-center gap-1"
                        title="Add a set"
                      >
                        <Plus size={10} /> Set
                      </button>
                      {rows.length !== parsePrescribedSetCount(ex.sets) && parsePrescribedSetCount(ex.sets) > 0 && (
                        <span className="text-[9px] font-mono-tir text-[var(--tir-muted)]" data-testid={`set-count-note-${key}`}>
                          {rows.length} of {parsePrescribedSetCount(ex.sets)} prescribed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {historyFor && (
        <HistoryModal
          exerciseName={historyFor}
          loading={historyLoading}
          rows={historyData}
          onClose={() => {
            setHistoryFor(null);
            setHistoryData([]);
          }}
        />
      )}

      {swapTargetKey && (() => {
        // Resolve the (exercise, block) pair from the chooser key so the
        // chooser knows what to swap *from* and what coach-defined
        // alternatives to surface.
        let chosenEx = null;
        let chosenBlock = null;
        for (const b of blocks) {
          for (const x of b.exercises || []) {
            if ((x.id || x.name) === swapTargetKey) {
              chosenEx = x;
              chosenBlock = b;
              break;
            }
          }
          if (chosenEx) break;
        }
        if (!chosenEx) return null;
        return (
          <SwapChooserModal
            exercise={chosenEx}
            onClose={() => setSwapTargetKey(null)}
            onPick={(name) => applySwap(swapTargetKey, chosenEx, chosenBlock, name)}
          />
        );
      })()}
    </div>
  );
}

function HistoryModal({ exerciseName, loading, rows, onClose }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[110] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="strength-history-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[var(--tir-border)] max-w-md w-full max-h-[80vh] overflow-auto"
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--tir-border)]">
          <div>
            <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold">
              History
            </div>
            <div className="font-serif-display text-lg text-[var(--tir-ink)]">
              {exerciseName}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)]">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {loading && (
            <div className="text-[12px] italic text-[var(--tir-muted)]">Loading…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-[12px] italic text-[var(--tir-muted)]">
              No previous sets logged for this exercise yet.
            </div>
          )}
          {!loading &&
            rows.map((r, i) => (
              <div
                key={i}
                className="border border-[var(--tir-border)] bg-[var(--tir-ivory)] p-2.5"
              >
                <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)]">
                  {(r.updated_at || "").slice(0, 10)}
                  <span className="ml-2 normal-case tracking-normal text-[var(--tir-muted)]/80">
                    · W{r.week_number} {r.day_label}
                  </span>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {(r.sets || []).map((s) => (
                    <div key={s.set_number} className="text-[12px] font-mono-tir">
                      <span className="text-[var(--tir-muted)] inline-block w-12">
                        Set {s.set_number}
                      </span>
                      {s.reps != null ? `${s.reps} reps` : "—"}
                      {s.weight != null ? ` × ${s.weight} ${s.weight_unit || "lbs"}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

/**
 * SwapChooserModal — lists coach-defined alternatives (from
 * `exercise.alternatives`) up top and curated system suggestions below.
 * Order-preserving de-dupe so a coach-defined "Push-up" wins over the
 * library's "Push-up" entry. Falls back to an inline "type your own"
 * field if none of the suggestions fit.
 */
function SwapChooserModal({ exercise, onClose, onPick }) {
  const [librarySuggestions, setLibrarySuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [custom, setCustom] = useState("");

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getStrengthSwapSuggestions(exercise.name || "", 8)
      .then((data) => {
        if (alive) setLibrarySuggestions(data?.suggestions || []);
      })
      .catch(() => alive && setLibrarySuggestions([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [exercise.name]);

  // Order-preserving de-dupe (case-insensitive). Coach picks first; the
  // library fills in the rest. We also drop the original exercise name
  // from suggestions so the athlete can't "swap" to the same exercise.
  const seen = new Set([(exercise.name || "").toLowerCase()]);
  const coachOpts = [];
  for (const c of (exercise.alternatives || [])) {
    const k = (c || "").trim();
    if (!k) continue;
    const lk = k.toLowerCase();
    if (seen.has(lk)) continue;
    seen.add(lk);
    coachOpts.push(k);
  }
  const libOpts = [];
  for (const s of librarySuggestions) {
    const lk = s.toLowerCase();
    if (seen.has(lk)) continue;
    seen.add(lk);
    libOpts.push(s);
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[120] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="strength-swap-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[var(--tir-border)] max-w-md w-full max-h-[80vh] overflow-auto"
      >
        <div className="flex items-start justify-between p-4 border-b border-[var(--tir-border)] gap-3">
          <div className="min-w-0">
            <div className="text-[9px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold">
              Swap exercise
            </div>
            <div className="font-serif-display text-lg text-[var(--tir-ink)] leading-tight">
              {exercise.name || "Exercise"}
            </div>
            <div className="text-[11px] text-[var(--tir-muted)] mt-1">
              Pick an alternative if you don't have the equipment or want to vary it. We'll log what you actually did.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {coachOpts.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] mb-2">
                Coach-approved swaps
              </div>
              <div className="flex flex-col gap-1.5">
                {coachOpts.map((opt) => (
                  <button
                    key={`coach-${opt}`}
                    type="button"
                    onClick={() => onPick(opt)}
                    data-testid={`swap-option-coach-${opt}`}
                    className="text-left px-3 py-2 border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] hover:bg-[var(--tir-ivory)] text-sm text-[var(--tir-ink)]"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(loading || libOpts.length > 0) && (
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-2">
                Common equivalents
              </div>
              {loading ? (
                <div className="text-[11px] italic text-[var(--tir-muted)]">Loading…</div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {libOpts.map((opt) => (
                    <button
                      key={`lib-${opt}`}
                      type="button"
                      onClick={() => onPick(opt)}
                      data-testid={`swap-option-lib-${opt}`}
                      className="text-left px-3 py-2 border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] hover:bg-[var(--tir-ivory)] text-sm text-[var(--tir-ink)]"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!loading && coachOpts.length === 0 && libOpts.length === 0 && (
            <div className="text-[12px] italic text-[var(--tir-muted)]">
              No suggestions for this exercise yet — type your own below.
            </div>
          )}
          <div className="border-t border-[var(--tir-border)] pt-3">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1.5">
              Or type your own
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = custom.trim();
                if (v) onPick(v);
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                data-testid="swap-custom-input"
                placeholder="e.g. Resistance Band Row"
                className="flex-1 border border-[var(--tir-border)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[var(--tir-burgundy)]"
              />
              <button
                type="submit"
                disabled={!custom.trim()}
                data-testid="swap-custom-submit"
                className="px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase font-semibold bg-[var(--tir-burgundy)] text-white disabled:opacity-40"
              >
                Swap
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

